/**
 * Per-(org, user, capability) configuration editor — the top override
 * layer of the init merge (defaults → global → org → user), applied to
 * tasks this user submits within the selected organization.
 *
 * Pick one of the account's organizations, then a capability; each field
 * row shows the value inherited from the org override, global default, or
 * capability default until the user layer overrides it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { ApiError } from '@optum/api-client/client';
import { getCapabilityGlobalConfig } from '@optum/api-client/endpoints/capability-global-configs';
import { getCapabilitySchema, listCapabilities } from '@optum/api-client/endpoints/capabilities';
import { getOrgCapabilityConfig } from '@optum/api-client/endpoints/org-capability-configs';
import { listOrgSecrets } from '@optum/api-client/endpoints/organization-secrets';
import {
  deleteUserCapabilityConfig,
  getUserCapabilityConfig,
  setUserCapabilityConfig,
} from '@optum/api-client/endpoints/user-capability-configs';
import type { AccountMembership, FieldSpec } from '@optum/api-client/types';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { Select } from '../../ui/Select.js';
import {
  ConfigFieldRow,
  clearStaleDependents,
  collectViolations,
  filterFrameworkFields,
  sanitizeAgainstSchema,
  type InheritedValue,
} from '../plugins/shared/init-config-form.js';

/** The baseline a field inherits when this user hasn't overridden it:
 *  the org override (layer 3) if present, else the global default
 *  (layer 2), else the capability's own schema default (layer 1). */
function inheritedFor(
  spec: FieldSpec,
  orgConfig: Record<string, unknown>,
  globalConfig: Record<string, unknown>,
): InheritedValue | undefined {
  if (Object.prototype.hasOwnProperty.call(orgConfig, spec.name)) {
    return { value: orgConfig[spec.name], label: 'organization override' };
  }
  if (Object.prototype.hasOwnProperty.call(globalConfig, spec.name)) {
    return { value: globalConfig[spec.name], label: 'global default' };
  }
  if (spec.has_default) return { value: spec.default, label: 'capability default' };
  return undefined;
}

export function UserCapabilityConfigEditor({
  platformUserId,
  memberships,
  organizationId,
}: {
  platformUserId: string;
  memberships: AccountMembership[];
  /** Fixes the organization and drops its picker, where the page already names it. */
  organizationId?: string | undefined;
}) {
  const activeMemberships = useMemo(
    () =>
      memberships.filter(
        (m) =>
          m.status === 'active' &&
          (organizationId === undefined || m.organization_id === organizationId),
      ),
    [memberships, organizationId],
  );
  const [orgId, setOrgId] = useState<string>('');
  const caps = useQuery({ queryKey: qk.capabilities(), queryFn: listCapabilities });
  const [capId, setCapId] = useState<string>('');

  const activeCaps = useMemo(
    () => (caps.data ?? []).filter((c) => c.status === 'active'),
    [caps.data],
  );

  useEffect(() => {
    if (orgId === '' && activeMemberships.length > 0) {
      setOrgId(activeMemberships[0]!.organization_id);
    } else if (orgId !== '' && !activeMemberships.some((m) => m.organization_id === orgId)) {
      setOrgId(activeMemberships[0]?.organization_id ?? '');
    }
  }, [activeMemberships, orgId]);

  useEffect(() => {
    if (capId === '' && activeCaps.length > 0) {
      setCapId(activeCaps[0]!.id);
    } else if (capId !== '' && !activeCaps.some((c) => c.id === capId)) {
      setCapId(activeCaps[0]?.id ?? '');
    }
  }, [activeCaps, capId]);

  if (activeMemberships.length === 0) {
    return <EmptyBlock>This account has no active memberships to configure.</EmptyBlock>;
  }
  if (caps.isPending) return <LoadingBlock />;
  if (caps.isError) {
    return (
      <ErrorBlock
        message={caps.error instanceof Error ? caps.error.message : 'Failed to load capabilities'}
      />
    );
  }
  if (activeCaps.length === 0) {
    return <EmptyBlock>No active capabilities to configure.</EmptyBlock>;
  }

  const org = activeMemberships.find((m) => m.organization_id === orgId) ?? activeMemberships[0]!;
  const selected = activeCaps.find((c) => c.id === capId) ?? activeCaps[0]!;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: organizationId === undefined ? 'repeat(2, minmax(0, 1fr))' : '1fr',
          gap: 11,
        }}
      >
        {organizationId === undefined ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <label style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
              Organization
            </label>
            <Select
              searchable
              value={org.organization_id}
              onChange={(value) => setOrgId(value)}
              options={[
                ...activeMemberships.map((m) => ({
                  value: m.organization_id,
                  label: m.organization_name,
                })),
              ]}
            />
          </div>
        ) : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <label style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
            Capability
          </label>
          <Select
            searchable
            value={selected.id}
            onChange={(value) => setCapId(value)}
            options={[
              ...activeCaps.map((c) => ({
                value: c.id,
                label: `${c.id} — ${c.name} · v${c.version}`,
              })),
            ]}
          />
        </div>
      </div>
      <UserConfigForm
        key={`${org.organization_id}:${selected.id}`}
        organizationId={org.organization_id}
        platformUserId={platformUserId}
        capabilityId={selected.id}
      />
    </div>
  );
}

function UserConfigForm({
  organizationId,
  platformUserId,
  capabilityId,
}: {
  organizationId: string;
  platformUserId: string;
  capabilityId: string;
}) {
  const schema = useQuery({
    queryKey: qk.capabilitySchema(capabilityId),
    queryFn: () => getCapabilitySchema(capabilityId),
  });
  const stored = useQuery({
    queryKey: qk.userCapConfig(organizationId, platformUserId, capabilityId),
    queryFn: () => getUserCapabilityConfig(organizationId, platformUserId, capabilityId),
  });
  const orgSecrets = useQuery({
    queryKey: qk.orgSecrets(organizationId),
    queryFn: () => listOrgSecrets(organizationId),
  });
  // Layers 2 + 3 of the merge — the inherited baseline for fields this
  // user hasn't overridden. 404 on the global row = no global default set.
  const orgConfig = useQuery({
    queryKey: qk.orgCapConfig(organizationId, capabilityId),
    queryFn: () => getOrgCapabilityConfig(organizationId, capabilityId),
  });
  const global = useQuery({
    queryKey: qk.capabilityGlobalConfig(capabilityId),
    queryFn: () => getCapabilityGlobalConfig(capabilityId),
    retry: (n, e) => (e instanceof ApiError && e.status === 404 ? false : n < 2),
  });

  const [values, setValues] = useState<Record<string, unknown>>({});
  const toast = useToast();
  useEffect(() => {
    if (!stored.data || !schema.data) return;
    const { cleaned, trimmed } = sanitizeAgainstSchema(stored.data.config, schema.data.init_fields);
    // Stored overrides for fields no longer user-enabled would 422 on save
    // (and are ignored at task time) — drop them from the editable set.
    const scopes = global.data?.field_scopes ?? {};
    const editable = Object.fromEntries(
      Object.entries(cleaned).filter(([name]) => scopes[name]?.user === true),
    );
    setValues(editable);
    if (trimmed.length > 0) {
      toast.info(
        'Trimmed stored values to schema',
        `${trimmed.join(', ')} — review and Save to persist.`,
      );
    }
  }, [stored.data, schema.data, global.data, toast]);

  const violations = useMemo(
    () => collectViolations(values, schema.data?.init_fields ?? []),
    [values, schema.data],
  );

  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () => setUserCapabilityConfig(organizationId, platformUserId, capabilityId, values),
    onSuccess: () => {
      toast.success('Saved');
      qc.invalidateQueries({
        queryKey: qk.userCapConfig(organizationId, platformUserId, capabilityId),
      });
    },
    onError: (e) => toast.error('Save failed', e instanceof ApiError ? e.message : String(e)),
  });
  const reset = useMutation({
    mutationFn: () => deleteUserCapabilityConfig(organizationId, platformUserId, capabilityId),
    onSuccess: () => {
      toast.success('Override cleared — the user inherits the organization config');
      setValues({});
      qc.invalidateQueries({
        queryKey: qk.userCapConfig(organizationId, platformUserId, capabilityId),
      });
    },
    onError: (e) => {
      // 404 = nothing to delete, which is fine — treat as already-clean.
      if (e instanceof ApiError && e.status === 404) {
        setValues({});
        toast.success('Already clean');
        return;
      }
      toast.error('Reset failed', e instanceof ApiError ? e.message : String(e));
    },
  });

  // Fields without the user scope are locked to the lower layers — hidden here.
  const initFields: FieldSpec[] = useMemo(
    () =>
      filterFrameworkFields(schema.data?.init_fields ?? []).filter(
        (f) => (global.data?.field_scopes ?? {})[f.name]?.user === true,
      ),
    [schema.data, global.data],
  );

  if (schema.isPending || stored.isPending || global.isPending) return <LoadingBlock />;
  if (schema.isError) {
    return (
      <ErrorBlock
        message={schema.error instanceof Error ? schema.error.message : 'Failed to load schema'}
      />
    );
  }
  if (stored.isError) {
    return (
      <ErrorBlock
        message={stored.error instanceof Error ? stored.error.message : 'Failed to load config'}
      />
    );
  }
  if (initFields.length === 0) {
    return (
      <EmptyBlock>
        No fields are enabled for user-level override. Enable them on the capability&apos;s global
        config.
      </EmptyBlock>
    );
  }

  const orgLayer = orgConfig.data?.config ?? {};
  const globalLayer = global.data?.config ?? {};

  return (
    <div style={{ borderRadius: 'var(--radius-chip)', border: '1px solid var(--border-hairline)' }}>
      <div
        style={{
          maxHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 11,
          overflowY: 'auto',
          padding: 11,
        }}
      >
        {initFields.map((spec) => (
          <ConfigFieldRow
            key={spec.name}
            spec={spec}
            value={values[spec.name]}
            siblings={values}
            {...(() => {
              const inh = inheritedFor(spec, orgLayer, globalLayer);
              return inh ? { inherited: inh } : {};
            })()}
            {...(violations[spec.name] !== undefined ? { violation: violations[spec.name]! } : {})}
            secretFieldKeys={(orgSecrets.data ?? []).map((e) => e.field_key)}
            onChange={(v) => {
              let next = { ...values };
              if (v === undefined) delete next[spec.name];
              else next[spec.name] = v;
              next = clearStaleDependents(initFields, next, spec.name);
              setValues(next);
            }}
          />
        ))}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 7,
          borderTop: '1px solid var(--border-hairline)',
          borderColor: 'var(--border-hairline)',
          padding: 11,
        }}
      >
        <Button
          variant="ghost"
          onClick={() => reset.mutate()}
          disabled={reset.isPending || save.isPending}
        >
          {reset.isPending ? 'Resetting…' : 'Reset to inherited'}
        </Button>
        <Button
          variant="primary"
          onClick={() => save.mutate()}
          disabled={save.isPending || Object.keys(violations).length > 0}
        >
          {save.isPending ? 'Saving…' : 'Save config'}
        </Button>
      </div>
    </div>
  );
}
